<script setup lang="ts">
import { computed, ref, watchEffect } from "vue";

const page = usePage().value;
const { gsap } = useGSAP();
const parseDate = (dateString: string | null) => {
  if (!dateString) return new Date(); // Fallback to the current date if null
  const parts = dateString.split("/").map(Number);
  const [day, month, year] = parts;
  if (day && month && year) {
    return new Date(year, month - 1, day);
  }
  return new Date(); // Fallback if parsing fails
};

interface Article {
  tags: string[];
  published: string;
  title: string;
  color?: string; // Optional if not all articles have a color defined
  uri?: string;
  blogImage?: {
    url: string;
    width: number;
    height: number;
    alt?: string;
  };
  blogImage1?: {
    url: string;
    width: number;
    height: number;
    alt?: string;
  };
}

const articles = computed<Article[]>(() => {
  return (page?.articles ?? [])
    .slice()
    .sort(
      (a: Article, b: Article) =>
        parseDate(b.published).getTime() - parseDate(a.published).getTime(),
    );
});

const selectedTag = ref("");
const keyword = ref("");

const filteredArticles = computed(() => {
  let filtered = articles.value;

  // Apply tag filter
  if (selectedTag.value !== "") {
    filtered = filtered.filter((article) =>
      article.tags.includes(selectedTag.value),
    );
  }

  // Apply keyword search filter
  if (keyword.value.trim() !== "") {
    const normalizedQuery = keyword.value.trim().toLowerCase();
    filtered = filtered.filter((article) =>
      article.title.toLowerCase().includes(normalizedQuery),
    );
  }

  return filtered;
});

const articlesPerPage = 9;
const displayedArticles = ref<Article[]>([]);

const loadMoreArticles = () => {
  const currentLength = displayedArticles.value.length;
  const newArticles = filteredArticles.value.slice(
    currentLength,
    currentLength + articlesPerPage,
  );
  displayedArticles.value = [...displayedArticles.value, ...newArticles];
};

watchEffect(() => {
  displayedArticles.value = filteredArticles.value.slice(0, articlesPerPage);
});

function onBeforeEnter(el: Element) {
  if (process.client && gsap.value) {
    gsap.value.set(el, {
      opacity: 0,
      scale: 0,
      x: 0,
      y: 500,
    });
  }
}

function onEnter(el: Element, done: () => void) {
  if (process.client && gsap.value) {
    gsap.value.to(el, {
      opacity: 1,
      scale: 1,
      x: 0,
      y: 0,
      duration: 0.8,
      delay: 0.25,
      onComplete: done,
    });
  } else {
    done();
  }
}

function onLeave(el: Element, done: () => void) {
  if (process.client && gsap.value) {
    gsap.value.to(el, {
      opacity: 0,
      scale: 0,
      x: 0,
      y: 0,
      duration: 0.2,
      delay: 0.25,
      onComplete: done,
    });
  } else {
    done();
  }
}
</script>

<template>
  <div class="article-search">
    <input
      v-model="keyword"
      type="text"
      placeholder="Search articles..."
      class="search-input"
    />
    <button v-if="keyword" class="clear-btn" @click="keyword = ''">✕</button>
  </div>
  <ClientOnly>
    <TransitionGroup
      tag="div"
      class="articles-wrap"
      :css="false"
      @before-enter="onBeforeEnter"
      @enter="onEnter"
      @leave="onLeave"
    >
      <div
        v-for="(article, index) in displayedArticles.slice(0, 3)"
        :key="article?.uri || `article-${index}`"
        class="article"
        :class="{ featured: index === 0 }"
      >
        <NuxtLink class="btn from-left" :to="`/${article?.uri}`">
          <div class="article-image">
            <NuxtPicture
              v-if="index === 0 ? article?.blogImage1 : article?.blogImage"
              format="webp"
              loading="lazy"
              :src="
                index === 0 ? article?.blogImage1?.url : article?.blogImage?.url
              "
              :width="
                index === 0
                  ? article?.blogImage1?.width
                  : article?.blogImage?.width
              "
              :height="
                index === 0
                  ? article?.blogImage1?.height
                  : article?.blogImage?.height
              "
              :alt="
                index === 0 ? article?.blogImage1?.alt : article?.blogImage?.alt
              "
              sizes="sm:100vw md:50vw lg:600px"
            />
          </div>
          <div class="article-meta">
            <h3>{{ article?.title }}</h3>
            <p class="date">
              {{ article?.published }}
            </p>
          </div>
        </NuxtLink>
      </div>
    </TransitionGroup>
  </ClientOnly>
  <ClientOnly>
    <TransitionGroup
      tag="div"
      class="articles-wrap2"
      :css="false"
      @before-enter="onBeforeEnter"
      @enter="onEnter"
      @leave="onLeave"
    >
      <div
        v-for="(article, index) in displayedArticles.slice(3)"
        :key="article?.uri || `article-${index + 3}`"
        class="article"
      >
        <NuxtLink class="btn from-left" :to="`/${article?.uri}`">
          <div class="article-image">
            <NuxtPicture
              v-if="article?.blogImage"
              format="webp"
              loading="lazy"
              :src="article?.blogImage.url"
              :width="article?.blogImage.width"
              :height="article?.blogImage.height"
              :alt="article?.blogImage.alt"
              sizes="sm:100vw md:50vw lg:600px"
            />
          </div>
          <div class="article-meta">
            <h3>{{ article?.title }}</h3>
            <p class="date">{{ article.published }}</p>
          </div>
        </NuxtLink>
      </div>
    </TransitionGroup>
  </ClientOnly>
  <div v-if="filteredArticles.length === 0" class="no-articles">
    <h3>No articles found.</h3>
  </div>

  <div class="pagination">
    <p>
      Showing 1 - {{ displayedArticles.length }} of
      {{ filteredArticles.length }} articles
    </p>
    <NuxtLink
      v-if="displayedArticles.length < filteredArticles.length"
      class="btn"
      @click.prevent="loadMoreArticles"
    >
      View more
    </NuxtLink>
  </div>
</template>
